import * as THREE from 'three';
import { TREASURE_TILE_CHARGE_MS } from './treasureReveal';

// A single reusable effect follows the active chest, including chained chests.
export const createTreasureTileScene = (board, surfaceY) => {
  const group = new THREE.Group();
  group.name = 'treasure-tile-awakening';
  group.visible = false;
  board.add(group);
  const surface = (opacity) =>
    new THREE.MeshBasicMaterial({
      color: '#ffe3a1',
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(2.55, 2.55),
    surface(0.3)
  );
  floor.rotation.x = -Math.PI / 2;
  group.add(floor);
  const frameBox = new THREE.BoxGeometry(2.65, 0.08, 2.65);
  const frame = new THREE.LineSegments(
    new THREE.EdgesGeometry(frameBox),
    new THREE.LineBasicMaterial({
      color: '#fff3ce',
      transparent: true,
      toneMapped: false,
    })
  );
  frameBox.dispose();
  group.add(frame);
  const rings = [0, 1, 2].map(() => {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.85, 1, 64),
      surface(0.7)
    );
    ring.rotation.x = -Math.PI / 2;
    group.add(ring);
    return ring;
  });
  const rays = Array.from({ length: 12 }, (_, index) => {
    const ray = new THREE.Mesh(new THREE.PlaneGeometry(0.035, 1), surface(0.4));
    const angle = (index * Math.PI) / 6;
    ray.position.set(Math.cos(angle) * 1.15, 0.5, Math.sin(angle) * 1.15);
    ray.rotation.y = -angle;
    group.add(ray);
    return ray;
  });
  const light = new THREE.PointLight('#ffdf85', 0, 6, 1.5);
  light.position.y = 1.5;
  group.add(light);
  let activeIndex = null;
  let startedAt = 0;
  let tileMaterial = null;
  const previousEmissive = new THREE.Color();
  let previousIntensity = 0;
  const restore = () => {
    if (!tileMaterial) return;
    tileMaterial.emissive.copy(previousEmissive);
    tileMaterial.emissiveIntensity = previousIntensity;
    tileMaterial = null;
  };
  return {
    update(index, elapsed, reduceMotion, tiles) {
      const valid = Number.isInteger(index) && !!tiles[index];
      group.visible = valid;
      if (!valid) {
        restore();
        activeIndex = null;
        return;
      }
      if (index !== activeIndex) {
        restore();
        activeIndex = index;
        startedAt = elapsed;
        tileMaterial = tiles[index].material;
        previousEmissive.copy(tileMaterial.emissive);
        previousIntensity = tileMaterial.emissiveIntensity;
        group.position.set(
          ((index % 5) - 2) * 2.85,
          surfaceY + 0.09,
          (Math.floor(index / 5) - 2) * 2.85
        );
      }
      const progress = reduceMotion
        ? 0.6
        : THREE.MathUtils.clamp(
            ((elapsed - startedAt) * 1000) / TREASURE_TILE_CHARGE_MS,
            0,
            1
          );
      const power = Math.sin((progress * Math.PI) / 2);
      tileMaterial.emissive.set('#ffcb69');
      tileMaterial.emissiveIntensity = 0.8 + power * 2;
      floor.material.opacity = 0.08 + power * 0.16;
      frame.material.opacity = 0.5 + power * 0.5;
      light.intensity = 4 + power * 5;
      rings.forEach((ring, i) => {
        ring.position.y = 0.02 + i * (reduceMotion ? 0.05 : progress * 0.75);
        ring.scale.setScalar(0.75 + power * 0.55 + i * 0.2);
        ring.material.opacity = (0.65 - i * 0.12) * power;
      });
      rays.forEach((ray, i) => {
        ray.visible = !reduceMotion;
        ray.scale.y = 0.2 + power * (1.2 + (i % 3) * 0.3);
        ray.position.y = ray.scale.y / 2;
        ray.material.opacity = 0.15 + power * 0.4;
      });
    },
  };
};
